import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DECK_SIZE,
  MAX_LIVES,
  MAX_PLAYERS,
  MIN_LIVES,
  MIN_PLAYERS,
  type PrivatePlayerSnapshot,
  type PublicRoomSnapshot,
  type RoomConfigPatch,
} from '@sincronia/shared';
import { Button, Overlay, Panel, Stepper, Toggle } from '../ui.js';
import { BrandMark, PlayerAvatar } from '../Brand.js';
import { QrCode } from '../QrCode.js';
import { errorKey, useT } from '../../i18n/index.js';
import { emitAck } from '../../net/socket.js';
import { copyText, roomUrl, shareRoom } from '../../lib/share.js';
import { clearSession } from '../../lib/session.js';
import { useGameStore } from '../../store/gameStore.js';
import { cn } from '../../lib/ui.js';

export function LobbyView({
  snapshot,
  privateState,
  isHost,
}: {
  snapshot: PublicRoomSnapshot;
  privateState: PrivatePlayerSnapshot | null;
  isHost: boolean;
}): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const publicAppUrl = useGameStore((state) => state.publicAppUrl);
  const pushToast = useGameStore((state) => state.pushToast);
  const reset = useGameStore((state) => state.reset);

  const [showQr, setShowQr] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const url = useMemo(() => roomUrl(snapshot.code, publicAppUrl), [snapshot.code, publicAppUrl]);
  const connectedCount = snapshot.players.filter((player) => player.connected).length;
  const canStart = isHost && connectedCount >= MIN_PLAYERS;
  const maxAllowedLevel = Math.max(1, Math.floor(DECK_SIZE / snapshot.config.capacity));

  async function patchConfig(patch: RoomConfigPatch): Promise<void> {
    const response = await emitAck<null>('room:updateConfig', { patch });
    if (!response.ok) setError(t(errorKey(response.error.code)));
    else setError(null);
  }

  async function handleCopy(): Promise<void> {
    const done = await copyText(url);
    setCopied(done);
    if (done) window.setTimeout(() => setCopied(false), 2200);
  }

  async function handleShare(): Promise<void> {
    const result = await shareRoom(url, t('lobby.title'), t('app.tagline'));
    if (result === 'copied') {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    }
  }

  async function handleStart(): Promise<void> {
    setBusy(true);
    const response = await emitAck<null>('room:start', {});
    setBusy(false);
    if (!response.ok) setError(t(errorKey(response.error.code)));
  }

  async function handleLeave(): Promise<void> {
    await emitAck<null>('room:leave', {});
    clearSession(snapshot.code);
    reset();
    void navigate('/');
  }

  async function handleRemove(playerId: string): Promise<void> {
    const response = await emitAck<null>('room:removePlayer', { playerId });
    if (!response.ok) pushToast(response.error.code);
  }

  return (
    <main className="safe-x safe-bottom safe-top mx-auto flex w-full max-w-md flex-1 flex-col gap-4 px-4 py-6">
      <header className="flex items-center gap-3">
        <BrandMark size={36} animated={false} />
        <h1 className="text-xl font-semibold text-night-200">{t('lobby.title')}</h1>
      </header>

      <Panel className="flex flex-col gap-4">
        <div className="text-center">
          <p className="text-xs tracking-widest text-night-400 uppercase">{t('lobby.code')}</p>
          <p
            className="tabular text-4xl font-bold tracking-[0.3em] text-night-200"
            data-testid="room-code"
          >
            {snapshot.code}
          </p>
        </div>

        <p
          className="rounded-xl bg-night-900/70 px-3 py-2 text-center text-xs break-all text-night-400"
          data-testid="room-link"
        >
          {url}
        </p>

        <div className="grid grid-cols-3 gap-2">
          <Button
            onClick={() => void handleCopy()}
            data-testid="copy-link"
            className="px-2 text-sm"
          >
            {copied ? t('lobby.copied') : t('lobby.copy')}
          </Button>
          <Button onClick={() => void handleShare()} className="px-2 text-sm">
            {t('lobby.share')}
          </Button>
          <Button
            onClick={() => setShowQr(true)}
            aria-label={t('lobby.qr')}
            data-testid="show-qr"
            className="px-2 text-sm"
          >
            QR
          </Button>
        </div>
      </Panel>

      <Panel className="flex flex-col gap-3">
        <h2 className="text-sm font-medium tracking-wide text-night-300 uppercase">
          {t('lobby.players', {
            count: snapshot.players.length,
            capacity: snapshot.config.capacity,
          })}
        </h2>
        <ul className="flex flex-col gap-2" data-testid="player-list">
          {snapshot.players.map((player) => (
            <li
              key={player.id}
              className={cn(
                'flex items-center gap-3 rounded-2xl bg-night-900/60 px-3 py-2',
                !player.connected && 'opacity-55',
              )}
            >
              <PlayerAvatar
                avatarKey={player.avatarKey}
                name={player.name}
                dimmed={!player.connected}
              />
              <span className="flex-1 truncate text-night-200">{player.name}</span>
              {player.id === privateState?.playerId ? (
                <span className="text-xs text-pulse-400">({t('common.you')})</span>
              ) : null}
              {player.isHost ? (
                <span className="rounded-full bg-nova-500/20 px-2 py-0.5 text-[11px] text-nova-400">
                  {t('lobby.host')}
                </span>
              ) : null}
              {!player.connected ? (
                <span className="text-[11px] text-life">{t('common.offline')}</span>
              ) : null}
              {isHost && player.id !== privateState?.playerId ? (
                <Button
                  variant="ghost"
                  className="min-h-9 px-2 py-1 text-xs"
                  aria-label={t('lobby.remove', { name: player.name })}
                  onClick={() => void handleRemove(player.id)}
                >
                  ✕
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium tracking-wide text-night-300 uppercase">
            {t('lobby.settings')}
          </h2>
          {isHost ? (
            <Button
              variant="ghost"
              className="min-h-9 px-2 py-1 text-xs"
              onClick={() => setShowAdvanced((value) => !value)}
              aria-expanded={showAdvanced}
              data-testid="toggle-advanced"
            >
              {t('lobby.advanced')}
            </Button>
          ) : null}
        </div>

        <p className="text-sm text-night-300" data-testid="rules-summary">
          {t('lobby.summary', {
            maxLevel: snapshot.config.maxLevel,
            lives: snapshot.config.startingLives,
            stars: snapshot.config.startingStars,
          })}
        </p>

        {isHost && showAdvanced ? (
          <div className="flex flex-col gap-3">
            <Toggle
              label={t('lobby.recommended')}
              description={t('lobby.recommendedHint')}
              checked={snapshot.config.useRecommended}
              onChange={(value) => void patchConfig({ useRecommended: value })}
            />
            <Stepper
              label={t('create.capacity')}
              value={snapshot.config.capacity}
              min={Math.max(MIN_PLAYERS, snapshot.players.length)}
              max={MAX_PLAYERS}
              onChange={(value) => void patchConfig({ capacity: value })}
            />
            <Stepper
              label={t('lobby.maxLevel')}
              value={snapshot.config.maxLevel}
              min={1}
              max={maxAllowedLevel}
              onChange={(value) => void patchConfig({ maxLevel: value })}
              disabled={snapshot.config.useRecommended}
            />
            <Stepper
              label={t('lobby.startingLives')}
              value={snapshot.config.startingLives}
              min={MIN_LIVES}
              max={MAX_LIVES}
              onChange={(value) => void patchConfig({ startingLives: value })}
              disabled={snapshot.config.useRecommended}
            />
            <Toggle
              label={t('lobby.sound')}
              checked={snapshot.config.soundEnabled}
              onChange={(value) => void patchConfig({ soundEnabled: value })}
            />
            <Toggle
              label={t('lobby.vibration')}
              checked={snapshot.config.vibrationEnabled}
              onChange={(value) => void patchConfig({ vibrationEnabled: value })}
            />
          </div>
        ) : null}
      </Panel>

      {error ? (
        <p role="alert" className="rounded-2xl bg-life/10 p-3 text-sm text-life">
          {error}
        </p>
      ) : null}

      <div className="mt-auto flex flex-col gap-3">
        {isHost ? (
          <>
            <Button
              variant="primary"
              fullWidth
              disabled={!canStart}
              loading={busy}
              onClick={() => void handleStart()}
              data-testid="start-game"
            >
              {t('lobby.start')}
            </Button>
            {!canStart ? (
              <p className="text-center text-xs text-night-400">{t('lobby.startHint')}</p>
            ) : null}
          </>
        ) : (
          <p className="text-center text-sm text-night-400" data-testid="waiting-host">
            {t('lobby.waitingHost')}
          </p>
        )}

        <Button variant="ghost" fullWidth onClick={() => void handleLeave()}>
          {t('lobby.leave')}
        </Button>
      </div>

      <Overlay
        open={showQr}
        title={t('lobby.qrTitle')}
        dismissible
        onClose={() => setShowQr(false)}
        testId="qr-modal"
      >
        <p className="mb-4 text-sm text-night-300">{t('lobby.qrHint')}</p>
        <QrCode value={url} />
        <Button className="mt-5" fullWidth onClick={() => setShowQr(false)}>
          {t('common.close')}
        </Button>
      </Overlay>
    </main>
  );
}
